import { AppLink } from '@/shared/components/AppLink';
import { cn } from '@/shared/utils/cn';

/** Matches API seoLinks shape: label, url, optional hoverTitle */
export interface ExploreOtherDesignsLink {
  label: string;
  url: string;
  hoverTitle?: string | null;
}

export interface ExploreOtherDesignsWidgetProps {
  /** Links shown in a horizontal scrollable row (not wrapped) */
  links: ExploreOtherDesignsLink[];
  /** Section title; default "Explore other designs" */
  title?: string;
  className?: string;
}

/**
 * Reusable "Explore other designs" widget: title + horizontal scrollable links.
 * Links use React Router (no full page refresh). Place above product lists or anywhere needed.
 */
export function ExploreOtherDesignsWidget({
  links,
  title = 'Explore other designs',
  className,
}: ExploreOtherDesignsWidgetProps) {
  if (links.length === 0) return null;

  return (
    <section
      className={cn('flex-shrink-0', className)}
      data-analytics-section-name="Explore other designs"
    >
      <div className="mx-auto px-4 pb-3 pt-3">
        <div className="flex items-center gap-3">
          <span className="shrink-0 whitespace-nowrap text-xs font-bold text-gray-900">
            {title}
          </span>
          <nav
            className="flex min-w-0 flex-nowrap gap-4 overflow-x-auto md:flex-wrap md:overflow-x-visible"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            aria-label={title}
          >
            {links.map((link, index) => (
              <AppLink
                key={`${link.url}-${index}`}
                to={link.url}
                title={link.hoverTitle ?? undefined}
                className="shrink-0 whitespace-nowrap border-b-2 border-transparent text-xs text-gray-500 transition-colors hover:border-[#4A607F] hover:text-[#4A607F]"
              >
                {link.label}
              </AppLink>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
}
